import {DataTable} from "../DataTable/DataTable";
import {DispatchDispatchRequestAssocEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {useMemo} from "react";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {
  DispatchDispatchRequestAssocStatusBadge
} from "../../badge/DispatchDispatchRequestAssocStatusBadge/DispatchDispatchRequestAssocStatusBadge";
import {DataTableColumn} from "mantine-datatable/dist/types/DataTableColumn";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {ActionIcon, Group} from "@mantine/core";
import {IconEye} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";
import {usePagination} from "../DataTable/usePagination";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: DispatchDispatchRequestAssocEntity[] = []

export type DispatchDispatchRequestAssocDataTableProps = EntityDataTableProps<DispatchDispatchRequestAssocEntity> & {
  showViewAction?: boolean;
}

export const DispatchDispatchRequestAssocDataTable = (props: DispatchDispatchRequestAssocDataTableProps) => {
  const {
    records: assocs = EMPTY_ARRAY,
    recordsPerPageOptions,
    showViewAction = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const {hasAccess} = useAuth()
  const navigate = useNavigate()

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: assocs,
    sortStatus: {columnAccessor: 'dispatchDttm', direction: 'desc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const cols: DataTableColumn<DispatchDispatchRequestAssocEntity>[] = [
      {
        title: t("common.entity.dispatchdispatchrequestassoc.table.column.scheduleddate", "Scheduled Date"),
        accessor: "dispatchDttm",
        width: 100,
        sortable: true,
        render: ({dispatchDttm}) => (
          dispatchDttm ? <FormattedDateTime date={dispatchDttm}/> : undefined
        ),
      },
      {
        title: t("common.entity.dispatchdispatchrequestassoc.table.column.status", "Status"),
        accessor: "status.code",
        width: 100,
        sortable: true,
        render: ({status}) => (
          <DispatchDispatchRequestAssocStatusBadge status={status}/>
        ),
      },
      {
        title: t("common.entity.dispatchdispatchrequestassoc.table.column.pickupdate", "Pickup Date"),
        accessor: "pickupDttm",
        width: 100,
        sortable: true,
        render: ({pickupDttm}) => (
          pickupDttm ? <FormattedDateTime date={pickupDttm}/> : undefined
        ),
      },
      {
        title: t("common.entity.dispatchdispatchrequestassoc.table.column.deliverydate", "Delivery Date"),
        accessor: "deliveryDttm",
        width: 100,
        sortable: true,
        render: ({deliveryDttm}) => (
          deliveryDttm ? <FormattedDateTime date={deliveryDttm}/> : undefined
        ),
      },
      {
        title: t("common.entity.dispatchdispatchrequestassoc.table.column.driver", "Driver"),
        accessor: "driverName",
        width: 100,
        sortable: true,
        render: ({driverId, driverName}) => (
          driverId ? <DefaultText value={driverName}/> : undefined
        ),
      },
    ]

    if (showViewAction && hasAccess("dispatch_view")) {
      cols.push({
        title: '',
        accessor: "actions",
        width: 100,
        sortable: true,
        render: ({dispatchId}) => (
          <Group spacing={4} position="right" noWrap>
            <ActionIcon color="primary" onClick={() => navigate(`/dispatch/profile?id=${dispatchId}`)}>
              <IconEye size={16}/>
            </ActionIcon>
          </Group>
        ),
      })
    }

    return cols
  }, [t, hasAccess, navigate, showViewAction])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  );
};
